import { type ReactNode } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { CalendarEventModel } from '../calendar/types';
import type { PressEvent } from '@react-types/shared';
import { ButtonBase } from '../components/buttons/ButtonBase';
import { tv } from 'tailwind-variants';
import { cn } from '../components/utils';

const eventChipStylees = tv({
	variants: {
		block: {
			true: 'flex w-full flex-col items-start gap-0.5 px-2 py-1 text-left text-xs font-medium'
		},
		draggable: {
			true: 'touch-none select-none [-webkit-touch-callout:none]'
		},
		isOverlay: {
			true: 'cursor-grabbing shadow-lg'
		},
		isDragging: {
			true: 'opacity-40'
		},
		isHighlighted: {
			true: 'ring-2 ring-primary-500 ring-offset-1'
		}
	},
	defaultVariants: {
		block: false,
		draggable: false,
		isOverlay: false,
		isDragging: false,
		isHighlighted: false
	}
});

export interface EventChipProps<T extends CalendarEventModel> {
	event: T;
	onClick?: (event: T, target: Element) => void;
	renderContent?: (event: T) => ReactNode;
	draggable?: boolean;
	isOverlay?: boolean;
	block?: boolean;
	className?: string;
}
export function EventChip<T extends CalendarEventModel>({
	event,
	onClick,
	renderContent,
	draggable = false,
	isOverlay = false,
	block = false,
	className
}: EventChipProps<T>) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: event.id,
		data: { event },
		disabled: !draggable || isOverlay || !event.canBeRescheduled
	});

	const handlePress = (e: PressEvent) => {
		onClick?.(event, e.target);
	};

	return (
		<ButtonBase
			ref={setNodeRef}
			data-event-chip
			data-event-id={event.id}
			onPress={handlePress}
			className={cn(
				eventChipStylees({
					block,
					draggable: draggable && !event.canBeRescheduled,
					isOverlay,
					isDragging,
					isHighlighted: event.isHighlighted
				}),
				className
			)}
			{...attributes}
			{...listeners}
			appearance={event.appearance}
			size={!block ? 'chip' : undefined}
			intent={event.intent}
		>
			{renderContent ? renderContent(event) : event.title}
			{event.Icons?.map(Icon => (
				<Icon key={Icon.displayName} className="size-3" />
			))}
		</ButtonBase>
	);
}
